import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { AuthService } from '../services/authService';

interface User {
  id: string;
  email?: string;
  full_name?: string;
  phone?: string;
  department?: string;
  modules_access?: string[];
}

interface Role {
  name: string;
  display_name: string;
}

interface AuthContextType {
  user: User | null;
  role: Role | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (accessCode: string, additionalData?: { full_name?: string; email?: string }) => Promise<LoginResult>;
  setGoogleToken: (token: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

interface LoginResult {
  success: boolean;
  message?: string;
  user?: User;
  role?: Role;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      // Sprawdź czy mamy zapisaną sesję w localStorage
      const savedSession = localStorage.getItem('plexiSession') || 
                         localStorage.getItem('plexisystem_session');
      
      if (savedSession) {
        try {
          const parsed = JSON.parse(savedSession);
          if (parsed.user && parsed.session) {
            console.log('✅ Restored session from localStorage');
            setUser(parsed.user);
            setRole({
              name: parsed.user.role || 'admin',
              display_name: 'Administrator'
            });
            
            // Ustaw token globalnie
            if (parsed.session.token && typeof window !== 'undefined') {
              (window as any).authToken = parsed.session.token;
            }
            
            setIsLoading(false);
            return;
          }
        } catch (e) {
          console.error('Error parsing saved session:', e);
        }
      }
      
      // Fallback do AuthService
      const isValid = await AuthService.checkSession();
      
      if (isValid) {
        const session = AuthService.getSession();
        if (session) {
          console.log('✅ Session found via AuthService');
          setUser(session.user);
          setRole({
            name: session.user.role,
            display_name: session.user.role === 'admin' ? 'Administrator' : 'Użytkownik'
          });
          
          const token = AuthService.getToken();
          if (token && typeof window !== 'undefined') {
            (window as any).authToken = token;
          }
        }
      }
    } catch (error) {
      console.error('Error checking session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (accessCode: string, additionalData?: { full_name?: string; email?: string }): Promise<LoginResult> => {
    // Oryginalna logika logowania
    try {
      setIsLoading(true);
      const userAgent = navigator.userAgent;
      
      const { data, error } = await supabase.rpc('login_with_access_code', {
        p_code: accessCode,
        p_full_name: additionalData?.full_name || null,
        p_email: additionalData?.email || null,
        p_user_agent: userAgent
      });

      if (error) {
        return {
          success: false,
          message: error.message
        };
      }

      // ... reszta oryginalnej logiki
      const user: User = {
        id: data.user_id || '1',
        email: additionalData?.email,
        full_name: additionalData?.full_name || 'Użytkownik',
        modules_access: ['wszystkie']
      };

      const role: Role = {
        name: data.role_name || 'user',
        display_name: data.role_display_name || 'Użytkownik'
      };

      const session = {
        user,
        role,
        session: {
          token: 'session-' + Date.now(),
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        }
      };

      localStorage.setItem('plexiSession', JSON.stringify(session));
      localStorage.setItem('plexisystem_session', JSON.stringify(session));
      
      setUser(user);
      setRole(role);

      return { success: true, user, role };
    } finally {
      setIsLoading(false);
    }
  };

  const setGoogleToken = async (token: string): Promise<LoginResult> => {
    try {
      setIsLoading(true);
      console.log('🔐 Google login processing...');
      
      const result = await AuthService.loginWithGoogle(token);
      
      if (result.success && result.user) {
        const user: User = {
          id: result.user.id,
          email: result.user.email,
          full_name: result.user.name,
          modules_access: result.user.modules_access || ['wszystkie']
        };
        
        const role: Role = {
          name: result.user.role || 'admin',
          display_name: 'Administrator'
        };
        
        // Zapisz sesję wszędzie
        const fullSession = {
          user,
          role,
          session: {
            token: AuthService.getToken() || 'google-token',
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          }
        };
        
        localStorage.setItem('plexiSession', JSON.stringify(fullSession));
        localStorage.setItem('plexisystem_session', JSON.stringify(fullSession));
        
        setUser(user);
        setRole(role);
        
        // Ustaw globalny token
        if (typeof window !== 'undefined') {
          (window as any).authToken = AuthService.getToken();
        }
        
        return { success: true, user, role };
      }
      
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setUser(null);
      setRole(null);
      await AuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const refreshSession = async () => {
    await checkSession();
  };

  const value: AuthContextType = {
    user,
    role,
    isAuthenticated: !!user,
    isLoading,
    login,
    setGoogleToken,
    logout,
    refreshSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useRole = () => {
  const { role } = useAuth();
  
  const isAdmin = () => role?.name === 'admin';
  const isKierownik = () => role?.name === 'kierownik_produkcji';
  const isHandlowiec = () => role?.name === 'handlowiec';
  const isOperator = () => role?.name === 'operator';
  const isMagazynier = () => role?.name === 'magazynier';
  const isKlient = () => role?.name === 'klient';
  
  return {
    role,
    isAdmin,
    isKierownik,
    isHandlowiec,
    isOperator,
    isMagazynier,
    isKlient
  };
};

export const useModuleAccess = () => {
  const { user, role } = useAuth();
  
  const hasModuleAccess = (moduleId: string): boolean => {
    if (role?.name === 'admin') return true;
    return user?.modules_access?.includes(moduleId) || user?.modules_access?.includes('wszystkie') || false;
  };
  
  return { hasModuleAccess };
};
